import { useCallback, useId } from "react";
import { useTranslation } from "react-i18next";
import { getExtraction } from "../../../api/knowledgeData/knowledgeDataApi";
import type { ThreadEntry } from "../../../api/threads/liveMessages";
import type { VitaAssembly } from "../../../api/threads/batchesApi";
import { useLoad } from "../../../shared/loading/useLoad";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import ABoxView from "../abox/ABoxView";
import { assembledVita, assembleResults } from "../abox/aboxViewModel";
import ResultDecision from "./ResultDecision";
import { recordedVitaDecision } from "./resultChoice";

interface AcceptVitaStepProps {
  readonly threadId: string;
  readonly entry: ThreadEntry;
  readonly assembly: VitaAssembly;
  /** The review asks for a comment with every decision (the common review part's rule, O1). */
  readonly commentRequired: boolean;
}

/**
 * "Accept the knowledge data" over an assembled vita (E4; review_vita): the vita assembled
 * from its parts' results, and one answer that decides every part. Accepting needs a vita
 * assembled from all its parts, current; the server checks it again when it decides.
 */
export default function AcceptVitaStep({ threadId, entry, assembly, commentRequired }: AcceptVitaStepProps) {
  const { t } = useTranslation();
  const headingId = useId();
  const load = useCallback(async (signal: AbortSignal) => {
    const results = await Promise.all(assembly.partOutputs.map((source) => getExtraction(source, signal)));
    const assembled = assembleResults(assembly, results);
    return { results, assembled, vita: assembledVita(assembly.vita, results, assembled.statements) };
  }, [assembly]);
  const { state, reload } = useLoad(load);
  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} />;
  const { results, vita } = state.value;
  // A decision begun on some parts is finished with the same answer, never decided afresh.
  const recorded = recordedVitaDecision(results);
  // The decision comes first, above the statements it decides on (usability round 1, 4 Nov).
  return (
    <div className="space-y-3">
      {!assembly.complete ? <p role="status" className="app-card p-3">{t("batches.partial")}: {t("batches.missingUnits")} {assembly.missingUnits.join(", ")}</p> : null}
      <section className="app-card space-y-2 p-3" aria-labelledby={headingId}>
        <h3 id={headingId} className="app-title text-base font-semibold">{t("data.curation.title")}</h3>
        <ResultDecision
          threadId={threadId}
          entry={entry}
          canDecide={recorded === undefined && results.length > 0 && results.every((result) => result.canDecide)}
          acceptable={assembly.complete}
          merge={{ newerCount: results.reduce((count, result) => count + result.lineage.pending.filter((pending) => pending.newer).length, 0) }}
          notCurator={recorded === undefined && results.some((result) => result.state === "proposed") ? t("data.lineage.notCurator") : null}
          recorded={recorded}
          commentRequired={commentRequired}
        />
      </section>
      <ABoxView extraction={vita} />
    </div>
  );
}
